/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * TypeScript/React version of DocIcon
 * Based on the original JSX version in /lego/page/browser/doc-icon/DocIcon.jsx
 *
 * Key differences from JSX version:
 * - Uses useNavigate hook from React Router v7
 * - Uses TypeScript interfaces for type safety
 * - Modern React patterns with proper typing
 * - Simplified service access (services may need to be implemented)
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { AclLevel, FsiType } from '../../constants/fsi'
import FsiExtImg from './FsiExtImg.js'
import FsiPropertiesModal from '../modals/FsiPropertiesModal'
import { useModal } from '~/contexts/ModalContext'
import { validateFileFactory } from '~/services/factory/validateFileFactory'

interface FsiAcl {
  level: string
}

interface FileSystemItem {
  id: string | number
  name?: string
  type?: string
  valid?: boolean
  systemFolderType?: string
  acl?: FsiAcl | string
  // Additional properties that might come from Document interface
  lastModified?: string | number
  [key: string]: any // Allow additional properties
}

interface DocIconProps {
  fsi: FileSystemItem
  inWorkflow?: boolean
  onClickDownload?: () => void
  onClickProperties?: () => void
}

const DocIcon: React.FC<DocIconProps> = ({ fsi, inWorkflow = false, onClickDownload, onClickProperties }) => {
  const navigate = useNavigate()
  const [acl, setAcl] = useState<string | null>(null)
  const { showModal } = useModal()
  const validateFile = validateFileFactory()

  const downloadFile = () => {
    // This would typically call a download service
    // downloadService.downloadOriginal(fsi.id);
    validateFile.downloadOriginal(Number(fsi.id))
  }

  const openPropertiesModal = () => {
    // This would typically open a properties modal
    showModal(
      <FsiPropertiesModal
        fsiId={fsi.id}
        onClose={function (): void {
          throw new Error('Function not implemented.')
        }}
      />
    )
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    if (!onClickDownload && !onClickProperties) {
      const fileType = fsi?.type || FsiType.DOCUMENT
      if (fileType === FsiType.FOLDER) {
        // Navigate to browser/folder view
        navigate(`/browser/${fsi.id}?type=${fsi.systemFolderType || ''}&page=1`)
      } else {
        // Navigate to file view
        navigate(`/show-file/${fsi.id}`)
      }
    } else if (onClickDownload) {
      downloadFile()
    } else if (onClickProperties) {
      openPropertiesModal()
    }
  }

  useEffect(() => {
    if (fsi?.acl) {
      // Handle both object with level property and direct string value
      const aclLevel = typeof fsi.acl === 'object' && fsi.acl.level ? fsi.acl.level : fsi.acl
      setAcl(aclLevel as string)
    } else {
      setAcl(null)
    }
  }, [fsi])

  // Determine if the icon should be clickable
  const fileType = fsi?.type || FsiType.DOCUMENT
  const isClickable = acl !== AclLevel.BROWSE && fileType !== FsiType.PLACEHOLDER && fsi.valid !== false // Default to true if valid is undefined

  return (
    <>
      {isClickable ? (
        <a
          href="#"
          onClick={handleClick}
          className="doc-icon-link"
          title={`Open ${fileType === FsiType.FOLDER ? 'folder' : 'file'}`}
        >
          <FsiExtImg fsi={fsi} inWorkflow={inWorkflow} />
        </a>
      ) : (
        <FsiExtImg fsi={fsi} inWorkflow={inWorkflow} />
      )}
    </>
  )
}

export default DocIcon
